/**
 * Page Transition System
 * Simple fade-out transition on page load without GSAP.
 * 
 * Flow:
 * 1. On page exit: Store random color, navigate normally (no transition)
 * 2. On page load: Solid color appears instantly (already visible)
 * 3. Color fades out over ~1.1s to reveal new page
 */

(function() {
  'use strict';

  const HOLD_DURATION_MS = 350;
  const FADE_OUT_DURATION_MS = 1100;

  let transitionOverlay = null;
  let transitionColors = [];

  /**
   * Initialize the page transition system
   */
  function init() {
    transitionColors = getTransitionColorsFromBody();

    if (transitionColors.length === 0) {
      console.warn('Page Transition: No accent colors configured');
      return;
    }

    transitionOverlay = document.getElementById('page-transition-overlay');
    if (!transitionOverlay) {
      createOverlay();
    }

    setupLinkColorStorage();

    handlePageEnter();
  }

  /**
   * Create the transition overlay element (fallback if not in HTML)
   */
  function createOverlay() {
    transitionOverlay = document.createElement('div');
    transitionOverlay.id = 'page-transition-overlay';
    transitionOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      pointer-events: none;
      opacity: 0;
      will-change: opacity;
      transform: translateZ(0);
    `;
    document.body.insertBefore(transitionOverlay, document.body.firstChild);
  }

  function getTransitionColorsFromBody() {
    const body = document.body;
    const fallbackColors = ['#1D2D75', '#8FAF3F', '#DAB07C'];
    const colors = [];

    // Only use the original theme accent colors (1-3)
    for (let i = 1; i <= 3; i += 1) {
      const value = body.getAttribute(`data-accent-color-${i}`);
      if (value && value.trim() !== '') {
        colors.push(value.trim());
      }
    }

    return colors.length > 0 ? colors : fallbackColors;
  }

  function shuffleArray(array) {
    const copy = [...array];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  function getNextTransitionColor() {
    const storageKeys = {
      lastColor: 'page-transition-last-color',
      pool: 'page-transition-color-pool'
    };

    let lastColor = null;
    let pool = [];

    try {
      lastColor = sessionStorage.getItem(storageKeys.lastColor);
      const storedPool = sessionStorage.getItem(storageKeys.pool);
      if (storedPool) {
        const parsedPool = JSON.parse(storedPool);
        if (Array.isArray(parsedPool)) {
          pool = parsedPool;
        }
      }
    } catch (e) {
      // Ignore storage parse issues and reset pool below.
    }

    const availableColors = transitionColors.filter(color => color !== lastColor);
    const validPool = pool.filter(color => availableColors.includes(color));

    if (validPool.length === 0) {
      pool = shuffleArray(availableColors.length > 0 ? availableColors : transitionColors);
    } else {
      pool = validPool;
    }

    const nextColor = pool.pop() || transitionColors[0];

    try {
      sessionStorage.setItem(storageKeys.pool, JSON.stringify(pool));
      sessionStorage.setItem(storageKeys.lastColor, nextColor);
    } catch (e) {
      // SessionStorage might not be available.
    }

    return nextColor;
  }

  /**
   * Set up link clicks to store color before navigation
   * No transition on exit - just store color for next page
   */
  function setupLinkColorStorage() {
    document.addEventListener('click', handleLinkClick, true);
  }

  /**
   * Handle link clicks - just store color, let browser navigate normally
   */
  function handleLinkClick(e) {
    const link = e.target.closest('a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    // Only handle internal links
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) {
        return; // External link, let browser handle it
      }

      // Skip hash links (anchor links)
      if (href.startsWith('#') || href === '') {
        return;
      }

      // Skip special links (mailto, tel, etc.)
      if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
        return;
      }

      // Skip if link has data-no-transition attribute
      if (link.hasAttribute('data-no-transition')) {
        return;
      }

      const color = getNextTransitionColor();
      storeTransitionColor(color);
      
      // Let browser handle navigation normally - no preventDefault.
    } catch (err) {
      // Invalid URL, let browser handle it.
      return;
    }
  }

  /**
   * Store transition color before navigation
   */
  function storeTransitionColor(color) {
    try {
      sessionStorage.setItem('page-transition-color', color);
    } catch (e) {
      // SessionStorage might not be available.
    }
  }

  function finishEnterTransition() {
    if (!transitionOverlay) {
      document.body.classList.add('scrollbar-ready');
      return;
    }

    transitionOverlay.style.transition = `opacity ${FADE_OUT_DURATION_MS}ms ease-out`;
    transitionOverlay.style.opacity = '0';

    window.setTimeout(function() {
      transitionOverlay.style.transition = '';
      document.body.classList.add('scrollbar-ready');
    }, FADE_OUT_DURATION_MS);
  }

  function handlePageEnter() {
    const storedColor = sessionStorage.getItem('page-transition-color');
    if (!storedColor) {
      if (transitionOverlay) {
        transitionOverlay.style.opacity = '0';
      }
      window.setTimeout(() => {
        document.body.classList.add('scrollbar-ready');
      }, 300);
      return;
    }

    if (transitionOverlay) {
      if (transitionOverlay.style.opacity !== '1') {
        transitionOverlay.style.backgroundColor = storedColor;
        transitionOverlay.style.opacity = '1';
      }
    }

    sessionStorage.removeItem('page-transition-color');

    window.requestAnimationFrame(function() {
      window.requestAnimationFrame(function() {
        window.setTimeout(finishEnterTransition, HOLD_DURATION_MS);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

